import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Loader2,
  MoreVertical,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDropzone } from 'react-dropzone';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import RoleSelect from '@/components/RoleSelect';
import {
  useUsers,
  useReferenceRoles,
  useUserDetail,
} from '../services/queries';
import {
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
} from '../services/mutations';
import { toast } from 'react-hot-toast';
import FormDrawer from '../components/FormDrawer';
import ConfirmationModal from '../components/ConfirmationModal';
import useAuth from '../hooks/useAuth';

const initialFormState = {
  first_name: '',
  last_name: '',
  username: '',
  password: '',
  email: '',
  roles: [],
  position: '',
  employee_id: '',
  credentials: [],
};

const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FORM':
      return { ...state, [action.field]: action.value };
    case 'RESET_FORM':
      return initialFormState;
    case 'SET_FORM_ALL':
      return action.payload;
    case 'SET_CREDENTIALS':
      return {
        ...state,
        credentials: state.credentials.map((cred) =>
          cred.application_code === action.payload.application_code
            ? { ...cred, ...action.payload }
            : cred,
        ),
      };
    case 'ADD_CREDENTIAL':
      if (
        !state.credentials.some(
          (cred) => cred.application_code === action.payload.application_code,
        )
      ) {
        return {
          ...state,
          credentials: [...state.credentials, action.payload],
        };
      }
      return state;
    case 'REMOVE_CREDENTIAL':
      return {
        ...state,
        credentials: state.credentials.filter(
          (cred) => cred.application_code !== action.payload,
        ),
      };
    default:
      return state;
  }
};

const UserManagement = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedUsername, setAppliedUsername] = useState('');
  const { isLoading, data, error, refetch } = useUsers(
    page,
    rowsPerPage,
    appliedUsername,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { data: dataRoles, refetch: refetchRoles } = useReferenceRoles();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const { checkPermissionElement } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [form, dispatch] = useReducer(formReducer, initialFormState);
  const [editMode, setEditMode] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [formError, setFormError] = useState(null);
  const [localData, setLocalData] = useState([]);
  const [loading, setLoading] = useState({ submit: false, delete: false });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [usernameToDelete, setUsernameToDelete] = useState(null);
  const [applications, setApplications] = useState([]);
  const [validationError, setValidationError] = useState({
    application_code: '',
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [file, setFile] = useState(null);

  const {
    data: userDetail,
    isFetching: isFetchingDetail,
    refetch: refetchUserDetail,
  } = useUserDetail(selectedUsername);

  // Initial fetch
  useEffect(() => {
    refetch();
    refetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Hanya pada mount awal

  // Refetch users ketika pagination atau filter berubah
  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, appliedUsername, refetch]); // Pagination and filter changes akan trigger refetch dengan query key baru

  // Handle data dengan struktur pagination yang fleksibel
  useEffect(() => {
    if (data) {
      // Support berbagai struktur response API
      // Struktur 1: { data: [...], total: number }
      // Struktur 2: { data: [...], meta: { total: number } }
      // Struktur 3: { users: [...], total: number }
      const usersData = data.data || data.users || data;
      setLocalData(Array.isArray(usersData) ? usersData : []);
    } else {
      setLocalData([]);
    }
  }, [data]);

  // Handle edit from UserDetail page
  useEffect(() => {
    if (location.state?.editUsername) {
      const user = data?.data?.find(
        (u) => u.username === location.state.editUsername,
      );
      if (user) {
        setSelectedUsername(location.state.editUsername);
        setEditMode(true);
        setBulkMode(false);
        setIsOpen(true);
      }
      // Clear the state to prevent reopening on subsequent navigations
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, data, navigate, location.pathname]);

  useEffect(() => {
    if (userDetail) {
      const { firstName, lastName } = splitFullName(userDetail.data.full_name);

      dispatch({
        type: 'SET_FORM_ALL',
        payload: {
          first_name: firstName || '',
          last_name: lastName || '',
          username: userDetail.data.username || '',
          password: '',
          email: userDetail.data.email || '',
          roles: userDetail.data.roles || [],
          position: userDetail.data.profile
            ? userDetail.data.profile.position || ''
            : '',
          employee_id: userDetail.data.profile
            ? userDetail.data.profile.employee_id || ''
            : '',
          credentials:
            userDetail.data.credentials &&
            userDetail.data.credentials.length > 0
              ? userDetail.data.credentials
              : initialFormState.credentials,
        },
      });

      const userApplications = userDetail.data.roles.reduce((acc, role) => {
        const roleDetail = dataRoles?.data?.find((r) => r.role === role);
        if (roleDetail) {
          roleDetail.applications.forEach((app) => {
            if (!acc.some((a) => a.application_code === app.code)) {
              acc.push({
                application_code: app.code,
                application_name: app.name,
                has_credentials: app.has_credentials,
              });
              if (!app.has_credentials) {
                dispatch({
                  type: 'ADD_CREDENTIAL',
                  payload: {
                    application_code: app.code,
                    username: '',
                    password: '',
                  },
                });
              }
            }
          });
        }
        return acc;
      }, []);
      setApplications(userApplications);
    }
  }, [userDetail, dataRoles]);

  const splitFullName = (fullName) => {
    const fullNameParts = fullName ? fullName.split(' ') : ['', ''];
    const firstName = fullNameParts[0];
    const lastName = fullNameParts.slice(1).join(' ');

    return { firstName, lastName };
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'SET_FORM', field: name, value });
    setValidationError({ ...validationError, [name]: '' });
  };

  const handleCredentialChange = (appCode, field, value) => {
    dispatch({
      type: 'SET_CREDENTIALS',
      payload: { application_code: appCode, [field]: value },
    });
    setValidationError({ ...validationError, [field]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const payload = {
      ...form,
      roles: form.roles !== undefined ? form.roles : [],
    };

    // let valid = true;
    // const newValidationError = {
    //   application_code: "",
    //   username: "",
    //   password: ""
    // };

    // form.credentials.forEach(cred => {
    //   if (!cred.application_code) newValidationError.application_code = "Application code is required";
    //   if (!cred.username) newValidationError.username = "Username is required";
    //   if (!cred.password) newValidationError.password = "Password is required";
    // });

    // setValidationError(newValidationError);
    // valid = Object.values(newValidationError).every(error => error === "");

    // if (!valid) return;

    payload.credentials = form.credentials.filter(
      (cred) => cred.username && cred.password,
    );

    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === '' ||
        (Array.isArray(payload[key]) && payload[key].length === 0)
      ) {
        delete payload[key];
      }
    });

    setLoading((prevLoading) => ({ ...prevLoading, submit: true }));
    try {
      const response = editMode
        ? await updateUser.mutateAsync({
            username: selectedUsername,
            body: payload,
          })
        : await createUser.mutateAsync(payload);

      console.log('Response dari server:', response);

      toast.success(
        editMode ? 'User updated successfully' : 'User created successfully',
        {
          className: 'toast-success',
        },
      );

      if (editMode) {
        refetchUserDetail();
      }

      setIsOpen(false);
      dispatch({ type: 'RESET_FORM' });
      refetch();
    } catch (error) {
      setFormError(error.response?.data?.detail || error.message);
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, submit: false }));
    }
  };

  const handleDelete = useCallback(
    (username) => {
      setUsernameToDelete(username);
      setIsModalOpen(true);
    },
    [setIsModalOpen, setUsernameToDelete],
  );

  const confirmDelete = async () => {
    setLoading((prevLoading) => ({ ...prevLoading, delete: true }));
    try {
      await deleteUser.mutateAsync(usernameToDelete);
      toast.success('User deleted successfully', {
        className: 'toast-success',
      });
      setIsModalOpen(false);
      refetch();
    } catch (error) {
      console.error('Error during user delete:', error);
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, delete: false }));
    }
  };

  const handleEdit = useCallback(
    (user) => {
      setSelectedUsername(user.username);
      setEditMode(true);
      setBulkMode(false);
      setIsOpen(true);
    },
    [setBulkMode, setEditMode, setIsOpen, setSelectedUsername],
  );

  const handleView = useCallback(
    (username) => {
      navigate(`/user-management/${username}`);
    },
    [navigate],
  );

  const handleAddSingleUser = () => {
    setSelectedUsername(null);
    setApplications([]);
    dispatch({ type: 'RESET_FORM' });
    setEditMode(false);
    setBulkMode(false);
    setIsOpen(true);
    setIsMenuOpen(false);
  };

  const handleAddBulkUsers = () => {
    setSelectedUsername(null);
    setApplications([]);
    dispatch({ type: 'RESET_FORM' });
    setEditMode(false);
    setBulkMode(true);
    setIsOpen(true);
    setIsMenuOpen(false);
  };

  const onDrop = useCallback((acceptedFiles) => {
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: '.xlsx, .xls, .csv',
  });

  // Pagination handlers untuk server-side pagination
  const handlePageChange = (value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleApplyFilter = () => {
    setAppliedUsername(searchQuery);
    setPage(1); // Reset to first page when applying filter
  };

  const handleClearFilter = () => {
    setSearchQuery('');
    setAppliedUsername('');
    setPage(1);
  };

  // Get total count and total pages from response API
  const totalCount = useMemo(() => {
    if (!data) return 0;
    return (
      data.total ||
      data.meta?.total ||
      data.pagination?.total ||
      localData.length
    );
  }, [data, localData.length]);

  const totalPages = useMemo(() => {
    return data?.meta?.total_pages || Math.ceil(totalCount / rowsPerPage);
  }, [data?.meta?.total_pages, totalCount, rowsPerPage]);

  // Data sudah di-paginate oleh server, jadi langsung gunakan
  const paginatedData = useMemo(() => {
    return localData || [];
  }, [localData]);

  const renderTableRows = useMemo(
    () =>
      paginatedData.length > 0 ? (
        paginatedData.map((user, i) => (
          <TableRow
            key={user.username}
            className={
              user.is_deleted ? 'line-through text-muted-foreground' : ''
            }
          >
            <TableCell className="w-[70px]">
              {(page - 1) * rowsPerPage + i + 1}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2 sm:gap-3">
                <img
                  src={user.picture_url}
                  alt="User Profile"
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-sm sm:text-base truncate">
                    {user.full_name}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {user.username}
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell>
              {user.profile?.employee_id || '-'}
            </TableCell>
            <TableCell>
              {user.profile?.position || '-'}
            </TableCell>
            <TableCell>
              {user.roles.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {user.roles.map((el) => (
                    <Badge key={el} variant="secondary" className="text-xs">
                      {el}
                    </Badge>
                  ))}
                </div>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell className="sticky-action w-[60px]">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={user.is_deleted}
                    className="h-8 w-8 sm:h-9 sm:w-9"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-1" align="end">
                  <div className="flex flex-col gap-0.5">
                    {checkPermissionElement(
                      'superapp:user-management:button:read',
                    ) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleView(user.username)}
                        className="justify-start h-8 px-2"
                      >
                        <Eye className="size-4 mr-2" />
                        View
                      </Button>
                    )}
                    {checkPermissionElement(
                      'superapp:user-management:button:edit',
                    ) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        className="justify-start h-8 px-2"
                      >
                        <Edit className="size-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {checkPermissionElement(
                      'superapp:user-management:button:delete',
                    ) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user.username)}
                        className="justify-start h-8 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="size-4 mr-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={6} className="text-center py-3">
            No users found
          </TableCell>
        </TableRow>
      ),
    [
      paginatedData,
      handleView,
      handleEdit,
      handleDelete,
      page,
      rowsPerPage,
      checkPermissionElement,
    ],
  );

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            isActive={i === page}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  };

  const handleToggleRole = (roleObj) => {
    const isSelected = form.roles.includes(roleObj.role);
    const updatedRoles = isSelected
      ? form.roles.filter((r) => r !== roleObj.role)
      : [...form.roles, roleObj.role];

    const selectedRoleDetails = updatedRoles
      .map((role) => dataRoles?.data?.find((r) => r.role === role))
      .filter(Boolean);

    const selectedApplications = selectedRoleDetails.reduce((acc, role) => {
      role.applications?.forEach((app) => {
        if (!acc.some((a) => a.application_code === app.code)) {
          acc.push({
            application_code: app.code,
            application_name: app.name,
            has_credentials: app.has_credentials,
          });
        }
      });
      return acc;
    }, []);

    setApplications(selectedApplications);

    let newCredentials = form.credentials.filter((cred) =>
      selectedApplications.some(
        (app) => app.application_code === cred.application_code,
      ),
    );

    selectedApplications.forEach((app) => {
      if (!app.has_credentials) {
        const exists = newCredentials.some(
          (cred) => cred.application_code === app.application_code,
        );
        if (!exists) {
          newCredentials.push({
            application_code: app.application_code,
            username: '',
            password: '',
          });
        }
      }
    });

    dispatch({ type: 'SET_FORM', field: 'roles', value: updatedRoles });
    dispatch({ type: 'SET_FORM', field: 'credentials', value: newCredentials });
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">
            User Management
          </h1>
          <p className="muted-text text-sm sm:text-base">
            Kelola pengguna, role, dan akses
          </p>
        </div>
        {checkPermissionElement('superapp:user-management:button:create') && (
          <Button
            className="justify-start h-9 sm:h-10 text-sm"
            onClick={handleAddSingleUser}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add User</span>
            <span className="sm:hidden">Add</span>
          </Button>
          // <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          //   <PopoverTrigger asChild>
          //     <Button className="flex items-center gap-2">
          //       <Plus className="h-4 w-4" />
          //       Add User

          //     </Button>
          //   </PopoverTrigger>
          //   <PopoverContent className="w-52 p-2">
          //     <div className="flex flex-col gap-1">
          //       <Button variant="ghost" className="justify-start" onClick={handleAddSingleUser}>
          //         Add Single User
          //       </Button>
          //       <Button variant="ghost" className="justify-start" onClick={handleAddBulkUsers}>
          //         Add Bulk Users
          //       </Button>
          //     </div>
          //   </PopoverContent>
          // </Popover>
        )}
      </div>

      <div className="section-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <Label htmlFor="username" className="mb-2 block text-sm">
              Username
            </Label>
            <Input
              id="username"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by username..."
              className="w-full h-9 sm:h-10"
            />
          </div>
          <div className="flex gap-2 sm:items-end sm:pb-0">
            <Button
              onClick={handleApplyFilter}
              className="flex-1 sm:flex-none h-9 sm:h-10 text-sm"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              onClick={handleClearFilter}
              className="flex-1 sm:flex-none h-9 sm:h-10 text-sm"
            >
              Clear
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        {isLoading ? (
          <div className="flex justify-center items-center mt-20">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            <div className="table-card">
              <div className="table-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px]">No</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>NIP</TableHead>
                      <TableHead>Jabatan</TableHead>
                      <TableHead>Roles</TableHead>
                      <TableHead className="sticky-action w-[60px]">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderTableRows}</TableBody>
                </Table>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 sm:mt-6 gap-3 sm:gap-4">
              <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start">
                <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                  Jumlah per halaman:
                </span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) =>
                    handleRowsPerPageChange(Number(value))
                  }
                >
                  <SelectTrigger className="w-[70px] sm:w-[80px] h-8 sm:h-10 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {totalPages > 1 && (
                <Pagination className="w-full sm:w-auto">
                  <PaginationContent className="gap-1">
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        className={`h-8 sm:h-10 text-xs sm:text-sm ${
                          page === 1
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }`}
                      />
                    </PaginationItem>
                    {getPaginationItems()}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          handlePageChange(Math.min(totalPages, page + 1))
                        }
                        className={`h-8 sm:h-10 text-xs sm:text-sm ${
                          page === totalPages
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }`}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}
      </div>

      <FormDrawer isOpen={isOpen} setIsOpen={setIsOpen}>
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[640px] flex flex-col h-full"
        >
          <div>
            <h3 className="text-xl font-semibold">
              {editMode
                ? 'Edit User'
                : bulkMode
                ? 'Add Bulk Users'
                : 'Add User'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {editMode
                ? 'Update user details and permissions.'
                : bulkMode
                ? 'Upload a file to create multiple users.'
                : 'Create a new user and assign roles.'}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto space-y-6">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            {isFetchingDetail ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <>
                {!bulkMode ? (
                  <>
                    <div className="space-y-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <span className="w-1 h-4 bg-primary rounded-full"></span>
                        Basic Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <Label htmlFor="employee_id">NIP</Label>
                          <Input
                            id="employee_id"
                            name="employee_id"
                            value={form.employee_id}
                            onChange={handleChangeInput}
                            className="mt-1.5"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="first_name">First Name</Label>
                            <Input
                              id="first_name"
                              name="first_name"
                              value={form.first_name}
                              onChange={handleChangeInput}
                              required
                              className="mt-1.5"
                            />
                          </div>
                          <div>
                            <Label htmlFor="last_name">Last Name</Label>
                            <Input
                              id="last_name"
                              name="last_name"
                              value={form.last_name}
                              onChange={handleChangeInput}
                              required
                              className="mt-1.5"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="username">Username</Label>
                          <Input
                            id="username"
                            name="username"
                            value={form.username}
                            onChange={handleChangeInput}
                            required
                            disabled={editMode}
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="password">Password</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={handleChangeInput}
                            required={!editMode}
                            className="mt-1.5"
                            placeholder={
                              editMode
                                ? 'Leave blank to keep current password'
                                : ''
                            }
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            name="email"
                            value={form.email}
                            onChange={handleChangeInput}
                            className="mt-1.5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="position">Jabatan</Label>
                          <Input
                            id="position"
                            name="position"
                            value={form.position}
                            onChange={handleChangeInput}
                            className="mt-1.5"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t">
                      <h4 className="font-semibold flex items-center gap-2">
                        <span className="w-1 h-4 bg-primary rounded-full"></span>
                        Roles and Applications
                      </h4>
                      <div className="space-y-3">
                        <RoleSelect
                          label="Assigned Roles"
                          placeholder={
                            form.roles.length > 0
                              ? `${form.roles.length} role(s) selected`
                              : 'Select roles'
                          }
                          roles={dataRoles?.data || []}
                          selectedRoles={form.roles}
                          onToggle={handleToggleRole}
                        />
                        
                        <div className="flex flex-wrap gap-2 min-h-[32px]">
                          {form.roles.length > 0 ? (
                            form.roles.map((role) => {
                              const detail = dataRoles?.data?.find(
                                (r) => r.role === role,
                              );
                              return (
                                <Badge
                                  key={role}
                                  variant="secondary"
                                  className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                                  onClick={() =>
                                    handleToggleRole(detail || { role })
                                  }
                                >
                                  {detail?.name || role} ×
                                </Badge>
                              );
                            })
                          ) : (
                            <span className="text-sm text-muted-foreground italic">
                              No roles selected
                            </span>
                          )}
                        </div>
                      </div>

                      {applications.filter((app) => !app.has_credentials)
                        .length > 0 && (
                        <div className="space-y-3">
                          <Label>Application Credentials</Label>
                          <Accordion
                            type="single"
                            collapsible
                            className="w-full border rounded-lg bg-card"
                          >
                            {applications
                              .filter((app) => !app.has_credentials)
                              .map((app) => (
                                <AccordionItem
                                  key={app.application_code}
                                  value={app.application_code}
                                  className="border-b last:border-0"
                                >
                                  <AccordionTrigger className="px-4 py-3 hover:bg-muted/50 transition-colors hover:no-underline">
                                    <div className="flex items-center gap-3">
                                      {/* Placeholder for app icon if available, using TVRI logo based on existing code */}
                                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center overflow-hidden">
                                        <img
                                          src="/tvri.jpg"
                                          alt="App"
                                          className="w-full h-full object-contain"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentNode.textContent =
                                              app.application_name?.charAt(0) ||
                                              'A';
                                          }}
                                        />
                                      </div>
                                      <div className="text-left">
                                        <div className="font-medium text-sm">
                                          {app.application_name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {app.application_code}
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="px-4 pb-4 pt-2 bg-muted/10">
                                    <div className="grid grid-cols-1 gap-3">
                                      <div>
                                        <Label className="mb-1.5 block text-xs font-medium">
                                          Username
                                        </Label>
                                        <Input
                                          value={
                                            form.credentials.find(
                                              (cred) =>
                                                cred.application_code ===
                                                app.application_code,
                                            )?.username || ''
                                          }
                                          onChange={(e) =>
                                            handleCredentialChange(
                                              app.application_code,
                                              'username',
                                              e.target.value,
                                            )
                                          }
                                          className="h-9 text-sm"
                                          placeholder={`Username for ${app.application_name}`}
                                        />
                                      </div>
                                      <div>
                                        <Label className="mb-1.5 block text-xs font-medium">
                                          Password
                                        </Label>
                                        <Input
                                          type="password"
                                          value={
                                            form.credentials.find(
                                              (cred) =>
                                                cred.application_code ===
                                                app.application_code,
                                            )?.password || ''
                                          }
                                          onChange={(e) =>
                                            handleCredentialChange(
                                              app.application_code,
                                              'password',
                                              e.target.value,
                                            )
                                          }
                                          className="h-9 text-sm"
                                          placeholder="Password"
                                        />
                                      </div>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                          </Accordion>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div
                      {...getRootProps({ className: 'dropzone' })}
                      className={`
                        p-10 border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer
                        ${
                          file
                            ? 'border-primary bg-primary/5'
                            : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
                        }
                      `}
                    >
                      <input {...getInputProps()} />
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <div className="p-3 rounded-full bg-muted">
                          {/* Icon placeholder */}
                          <Plus className="h-6 w-6" />
                        </div>
                        <p className="font-medium">
                          Drag and drop file here, or click to select
                        </p>
                        <p className="text-xs">
                          Supported formats: .xlsx, .xls, .csv
                        </p>
                      </div>
                    </div>
                    {file && (
                      <div className="flex items-center gap-3 p-3 border rounded-lg bg-card">
                        <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center text-primary font-bold">
                          F
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <p className="text-sm font-medium truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setFile(null)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="pt-3 border-t bg-muted/10 flex justify-end gap-3 mt-auto">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading.submit}>
              {loading.submit && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editMode
                ? 'Save Changes'
                : bulkMode
                ? 'Upload & Create'
                : 'Create User'}
            </Button>
          </div>
        </form>
      </FormDrawer>

      <ConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        description="Are you sure you want to delete this user?"
      />
    </div>
  );
};

export default UserManagement;
